/**
 * Print order (the Oct 21 canvas, the kitchen's day: shop → prep → cook → pack → deliver): the day cover, the
 * shopping list (milk, ingredients and supplies on one list), production totals, the prep recipes, the pull & pack
 * list, then the stop sheets.
 * The separate delivery route page is gone: its code is on the cover. Stable within each kind, multi-day included.
 */

import { coverSection, withContents } from './cover'
import { type PacketDay, packetDay } from './day'
import { pullGroups, pullItemCount, pullPackSection } from './pull-pack'
import { recipeSections } from './recipes'
import { shoppingListSection } from './shopping'
import { stopSummarySectionId, stopSummarySections } from './stop-summaries'
import { productionSummarySections } from './summaries'
import type { PacketTarget } from './target'
import type { PacketModel, PacketSection } from './units'

/** A short, stable digest (djb2) of the model's data: the measurement key. */
export function digest(text: string): string {
  let hash = 5381
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0
  }
  return `${(hash >>> 0).toString(36)}.${text.length.toString(36)}`
}

function daySections(day: PacketDay, target: PacketTarget, multiDay: boolean): PacketSection[] {
  const on = target.sections
  const shopping = on.ordering && !multiDay ? shoppingListSection([day], target.kitchenName, false) : null
  const recipes = on.production ? recipeSections(day, target.measurement) : null
  const pulls = on.production ? pullGroups(day) : []
  // The Pull List sends the packer to the stop sheets the driver carries: the kitchen copies when they print.
  const sheetIds = on.delivery
    ? day.stops.map((stop) =>
        stopSummarySectionId(day.iso, stop.identity.number, on.kitchenCopy ? 'kitchen' : 'school'),
      )
    : []
  const pull = on.production ? pullPackSection(day, pulls, sheetIds) : null
  const sections = [
    ...(shopping ? [shopping] : []),
    ...(on.production ? productionSummarySections(day, pull?.id ?? null) : []),
    ...(recipes ? [...recipes.hot, ...recipes.cold] : []),
    ...(pull ? [pull] : []),
    ...(on.delivery ? stopSummarySections(day, on.kitchenCopy) : []),
  ]
  if (sections.length === 0 || day.stops.length === 0) {
    return sections
  }
  const recipeCount = recipes ? recipes.hot.length + recipes.cold.length : 0
  const cover = coverSection(day, target.routeLinkOrigin ?? null, {
    recipes: recipeCount,
    pullItems: pullItemCount(pulls),
  })
  return [withContents(cover, sections), ...sections]
}

/** The packet for a target, sections in print order. */
export function buildPacketModel(target: PacketTarget): PacketModel {
  const days = target.days.filter((day) => day.campuses.length > 0)
  const packetDays = days.map((day) => packetDay(day, target.route, target.kitchenName))
  const multiDay = packetDays.length > 1
  const rangeShopping =
    target.sections.ordering && multiDay ? shoppingListSection(packetDays, target.kitchenName, true) : null
  const sections: PacketSection[] = [
    ...(rangeShopping ? [rangeShopping] : []),
    ...packetDays.flatMap((day) => daySections(day, target, multiDay)),
  ]
  const priority: Record<string, number> = {
    cover: 0,
    'shopping-list': 1,
    'production-summary': 3,
    recipe: 5,
    'pull-pack': 6,
    'stop-summary': 7,
  }
  sections.sort((a, b) => (priority[a.kind] ?? 5) - (priority[b.kind] ?? 5))
  // The preliminary labels print on the bands of the pages they mark, so they are part of what was measured.
  const signature = digest(JSON.stringify({ sections, preliminary: target.preliminary ?? null }))
  return { sections, signature, dayCount: packetDays.length }
}

const MODELS = new WeakMap<PacketTarget, PacketModel>()

/** `buildPacketModel`, once per target object. */
export function packetModelFor(target: PacketTarget): PacketModel {
  let model = MODELS.get(target)
  if (!model) {
    model = buildPacketModel(target)
    MODELS.set(target, model)
  }
  return model
}
